import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CheckCircle2Icon, GiftIcon } from 'lucide-react';
import { PreviewCarousel } from './PreviewCarousel';
import { bonuses, volumes } from '../data/offer';
import type { Volume } from '../types/content';
// Each volume maps to one of the existing accent tokens.
const accentStyles: Record<Volume['accent'], {
  border: string;
  text: string;
}> = {
  info: {
    border: 'border-info/30',
    text: 'text-info'
  },
  primary: {
    border: 'border-primary-soft/30',
    text: 'text-primary-soft'
  },
  highlight: {
    border: 'border-highlight/30',
    text: 'text-highlight'
  },
  danger: {
    border: 'border-danger-soft/30',
    text: 'text-danger-soft'
  }
};
export function ProductOffer() {
  return <>
      <section className="bg-surface px-4 py-16 sm:py-24" data-id="element-147" data-mp="fj57v9hrp24a">
        <div className="mx-auto max-w-5xl" data-id="element-148" data-mp="fjol4cwh32bz">
          <header className="text-center" data-id="element-149" data-mp="khyimu5o6f0r">
            <h2 className="font-display text-3xl font-bold text-highlight sm:text-5xl" data-id="element-150" data-mp="khq653dglbq3">
              🏆 PROTOCOLO CALMA APLICADA
            </h2>
            <p className="mx-auto mt-4 max-w-4xl text-lg font-medium" data-id="element-151" data-mp="3gfykwz3503bg">
              Tu sistema en 4 volúmenes para soltar lo que no ayuda, ordenar lo que pasa y seguir con más calma y claridad.
            </p>
          </header>
          <h3 className="mt-12 text-center font-display text-2xl font-bold" data-id="element-152" data-mp="3qdw7zj99fu2h">
            📚 4 VOLÚMENES COMPLETOS
          </h3>
          <div className="mt-7 space-y-5" data-id="element-153" data-mp="32p3edtg41yb1">
            {volumes.map((volume, index) => {
            const accent = accentStyles[volume.accent];
            return <motion.article key={volume.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.06
            }} className={`flex gap-5 rounded-2xl border ${accent.border} bg-canvas p-5 sm:p-7`} data-id="element-154" data-mp="3bhf1b5c0n3m9" data-mpi={index}>
                  <BookOpenIcon className={`mt-1 h-7 w-7 shrink-0 ${accent.text}`} aria-hidden="true" data-id="element-155" data-mp="hywey4op78y6" />
                  <div className="flex-1" data-id="element-156" data-mp="mwxvahuqou5y">
                    <p className={`text-xs font-bold tracking-brand ${accent.text}`} data-id="element-157" data-mp="mvmtuoornyi0">
                      {volume.label}
                    </p>
                    <h4 className="mt-1 font-display text-lg font-bold sm:text-xl" data-id="element-158" data-mp="wmziz8j6kakc">
                      {volume.title}
                    </h4>
                    {volume.subtitle && <p className="mt-1 text-sm text-foreground-subtle sm:text-base" data-id="element-159" data-mp="3v6zjfu0l3aev">
                        {volume.subtitle}
                      </p>}
                    <ul className="mt-4 space-y-2" data-id="element-160" data-mp="8wn57h197jlu">
                      {volume.items.map(item => <li key={item} className="flex gap-2 text-sm leading-relaxed text-foreground-muted sm:text-base" data-id="element-161" data-mp="dxau9x5x9qdf">
                          <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary-soft" aria-hidden="true" data-id="element-162" data-mp="1kbsb0twoly9o" />
                          {item}
                        </li>)}
                    </ul>
                    <p className="mt-5 border-t border-foreground/10 pt-4 text-sm sm:text-base" data-id="element-163" data-mp="39sxniidrsv9q">
                      <span className="font-semibold text-foreground-subtle" data-id="element-164" data-mp="34vxhg7gx7sim">Resultado: </span>
                      <span className={`font-bold italic ${accent.text}`} data-id="element-165" data-mp="3034b5f5t0c4f">{volume.result}</span>
                    </p>
                  </div>
                </motion.article>;
          })}
          </div>
        </div>
      </section>
      <PreviewCarousel data-id="element-166" data-mp="2q7b0607qgqor" />
      <section className="bg-canvas px-4 py-16 sm:py-24" data-id="element-167" data-mp="2q3n8z7prggd7">
        <div className="mx-auto max-w-5xl" data-id="element-168" data-mp="2m3lat8j8i52o">
          <header className="text-center" data-id="element-169" data-mp="2h5a5nb3vwiqs">
            <GiftIcon className="mx-auto h-10 w-10 text-highlight" aria-hidden="true" data-id="element-170" data-mp="2h632nkd1jtvq" />
            <h2 className="mt-3 font-display text-3xl font-bold text-highlight sm:text-5xl" data-id="element-171" data-mp="2c7050kze4pxs">
              5 BONOS DE REGALO
            </h2>
          </header>
          <div className="mt-10 space-y-4" data-id="element-172" data-mp="aeu6zxrdzr22">
            {bonuses.map(({
            number,
            title,
            items,
            value
          }) => <article key={number} className="flex flex-col gap-4 rounded-2xl border border-highlight/20 bg-surface p-5 sm:flex-row sm:items-start sm:p-7" data-id="element-173" data-mp="3484x86zgez6j">
                <span className="w-fit whitespace-nowrap rounded-lg bg-highlight/10 px-3 py-2 font-bold text-highlight" data-id="element-174" data-mp="2u8w75p7pm4pg">
                  🎁 BONO {number}
                </span>
                <div className="flex-1" data-id="element-175" data-mp="2z9jx07k7cfi3">
                  <h3 className="font-bold" data-id="element-176" data-mp="2kikpkzv3rx03">{title}</h3>
                  
                  <ul className="mt-3 space-y-1.5" data-id="element-177" data-mp="2p9axajr04bxy">
                    {items.map(item => <li key={item} className="flex gap-2 text-sm leading-relaxed text-foreground-muted" data-id="element-178" data-mp="2pbnpdpqh42oq">
                        <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-highlight" aria-hidden="true" data-id="element-179" data-mp="2bgbjqnas4rt1" />
                        {item}
                      </li>)}
                  </ul>
                </div>
                <div className="flex shrink-0 items-center gap-3" data-id="element-180" data-mp="26j1t54bnma3m">
                  <span className="text-xs font-semibold text-foreground-faint" data-id="element-181" data-mp="26ia3090tt4kq">VALOR:</span>
                  <span className="text-sm text-foreground-faint line-through" data-id="element-182" data-mp="1mxjktnd28u3e">
                    {value}
                  </span>
                  <span className="rounded-full bg-primary-soft px-3 py-1 text-sm font-bold text-canvas" data-id="element-183" data-mp="1wvfu09mfteyv">
                    GRATIS
                  </span>
                </div>
              </article>)}
          </div>
          <div className="mt-8 rounded-2xl border border-highlight/30 bg-highlight/10 p-6 text-center" data-id="element-184" data-mp="25vm19drjlipl">
            <p className="text-xs font-bold tracking-brand text-highlight" data-id="element-185" data-mp="21vby08v4x6ef">
              VALOR TOTAL BONOS
            </p>
            <p className="mt-1 text-2xl font-bold text-foreground-subtle line-through" data-id="element-186" data-mp="2fkm5bbkwj4an">
              $52.000
            </p>
            <p className="mt-2 font-display text-xl font-bold text-highlight" data-id="element-187" data-mp="2aoxbzpzh6jms">
              GRATIS con tu Protocolo Calma Aplicada
            </p>
          </div>
        </div>
      </section>
    </>;
}